import { Injectable } from '@angular/core';
import { GroupComponent } from 'tabulator-tables';
import { Subject } from 'rxjs';

import { LogoWidgetService } from '../../components/logo.widget/logo.widget.service';
import { DateFormatterService } from '../date.formatter/date.formatter.service';
import { actionConfiguration, iconConfiguration } from './icon-configuration';
import { FormatService } from '../../services/format.service';
import { UNIT } from '../../models/types';
import { round } from 'lodash';
import DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root'
})
export class TabulatorService {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  menu = actionConfiguration[0]['ActiveTests'];
  // eslint-disable-next-line
  temp: { label: string; id: string; rowData: any };
  resolvedImageUrl: string;
  // eslint-disable-next-line
  public addToCartClicked: Subject<any> = new Subject();
  constructor(
    private readonly dateFormatService: DateFormatterService,
    private readonly logoWidgetService: LogoWidgetService,
    private readonly formatService: FormatService
  ) {}
  // eslint-disable-next-line
  getLink(cell, formatterParams) {
    const rowData = cell.getRow().getData();
    if (rowData.notAvailed) {
      return '';
    } else if (!rowData.removed) {
      return '<a id="cart_remove" class="btn-link">Remove</a>';
    } else {
      return '<a id="cart_remove" class="btn-link">Undo</a>';
    }
  }
  // eslint-disable-next-line
  getNameWithAlertMessage(cell, formatterParams) {
    const rowData = cell.getRow().getData();

    let htmlStr = `<div>${rowData.name.label}</div>`;
    if (rowData.notAvailed) {
      htmlStr = `<div class="text-light">${rowData.name.label}</div><div class="err-text">${rowData.errMsg}</div>`;
    } else if (rowData.removed) {
      const err = 'Item removed';
      htmlStr = `<div class="text">${rowData.name.label}</div><div class="removed-text">${err}</div>`;
    }
    return htmlStr;
  }
  // eslint-disable-next-line
  getLabel(cell, formatterParams) {
    const rowData = cell.getRow().getData();
    const label = rowData[formatterParams.columnName]?.label
      ? rowData[formatterParams.columnName].label
      : '';
    let htmlStr = `<div>${label}</div>`;
    if (rowData.removed) {
      htmlStr = `<div class="text">${label}</div>`;
    }
    return htmlStr;
  }

  //eslint-disable-next-line
  deepMatchHeaderFilter = (headerValue, rowValue, rowData, filterParams): boolean => {
    const columnName = filterParams.extraValueForFilter?.[0].fields;

    let anyChildMatch = false;
    const childData = rowData._children || [];
    if (columnName === 'reflectorType') {
      childData.forEach(data => (data['filtered'] = false));
    }
    const filteredChildren = childData.some(data => data.filtered)
      ? childData.filter(data => data.filtered)
      : childData;
    for (const childRow of filteredChildren || []) {
      // We walk down the tree recursively
      const match = this.deepMatchHeaderFilter(
        headerValue,
        childRow[columnName].label,
        childRow,
        filterParams
      );
      if (match) {
        anyChildMatch = true;
        childRow['filtered'] = true;
      } else {
        childRow['filtered'] = false;
      }
    }

    // If any child (and therefore any descendant) matched, we return true.
    if (anyChildMatch) {
      return true;
    }

    // We run the actual maching test where applicable. This could be a customised function
    //(passed in the filterParams, for example).
    if (rowValue != null && rowValue.toString().toLowerCase().includes(headerValue.toLowerCase())) {
      return true;
    }
    const subLabel = rowData[columnName]?.sublabel;
    if (
      subLabel &&
      rowValue != null &&
      subLabel.toString().toLowerCase().includes(headerValue.toLowerCase())
    ) {
      return true;
    }

    return false;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getPmStatsIcon = (cell, formatterParams): string => {
    return '<i class="mdi mdi-view-list"></i>';
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getCellCustomClass = (cell, formatterParams): any => {
    const cellValue = cell.getRow().getData()[formatterParams.columnName]?.value || '';
    const className = cell.getRow().getData()[formatterParams.columnName]?.class || '';
    if (className) {
      cell.getElement().classList.add(className);
    }
    return cellValue;
  };

  // eslint-disable-next-line
  formatHeaderWithMetrics = (cell, formatterParams): any => {
    let titleFormat = '';
    if (formatterParams?.title) {
      titleFormat = `<div>${formatterParams.title}</div>`;
    }
    if (formatterParams?.subTitle) {
      titleFormat += `<div class='header-subtitle'>(${formatterParams.subTitle})</div>`;
    }
    return titleFormat;
  };

  // eslint-disable-next-line
  getMatIcon = (cell, formatterParams): string => {
    let matIcon = '';
    const cellValue = cell.getRow().getData()[formatterParams.columnName];
    if (cellValue?.icon) {
      const iconName = cellValue?.icon;
      const customClass = cellValue?.customClass || '';
      matIcon = '<i class="tabulator-icon mdi ' + iconName + ' ' + customClass + '"></i>';
    }
    return matIcon;
  };

  // eslint-disable-next-line
  getMatIconWithText = (cell, formatterParams): string => {
    let matIcon = '';
    let text = '';
    let subText = '';
    const cellValue = cell.getRow().getData()[formatterParams.columnName];
    if (cellValue?.icon) {
      const iconName = cellValue?.icon;
      const customClass = cellValue?.customClass || '';
      matIcon = '<i class="tabulator-icon mdi ' + iconName + ' ' + customClass + '"></i>';
      text = cellValue.text ? cellValue.text : '';
      subText = cellValue.subText ? cellValue.subText : '';
    }
    return `<div class="icon-text">
      <div class="icon-wrapper">${matIcon}</div>
      <div class="icon-text-wrapper">
        <div class="main-title truncated-text">${text}</div>
        <div class="sub-title truncated-text">${subText}</div>
      </div>
      </div>`;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getIcon = (cell, formatterParams): string => {
    const serviceType = DOMPurify.sanitize(cell.getValue()) || '';
    const subLabel =
      DOMPurify.sanitize(cell.getRow().getData()[formatterParams.columnName]?.sublabel) || '';
    const subLabel1 =
      DOMPurify.sanitize(cell.getRow().getData()[formatterParams.columnName]?.sublabel1) || '';
    const subLabelText =
      DOMPurify.sanitize(cell.getRow().getData()[formatterParams.columnName]?.sublabeltext) || '';
    const cellValue = DOMPurify.sanitize(cell.getRow().getData()[formatterParams.columnName]);
    let icon = '';
    if (cellValue?.url) {
      const imageURL = this.getImageURL(cellValue);
      icon = `<img class="tabulator-icon" alt="" src=${imageURL.image}>`;
    } else {
      const selectedTableConfig = iconConfiguration[0][formatterParams.tableName];
      const iconType = cell.getRow().getData()[formatterParams.columnName]?.iconType || serviceType;
      const index = selectedTableConfig?.findIndex(x => x.key == iconType);
      switch (iconType) {
        case iconType:
          if (index !== undefined && index !== -1 && selectedTableConfig) {
            if (selectedTableConfig[index].iconValue) {
              icon =
                '<i class="tabulator-icon mdi ' +
                selectedTableConfig[index].iconValue +
                ' ' +
                selectedTableConfig[index]?.customClass +
                '"></i>';
            } else {
              icon =
                '<img class="tabulator-icon" src="images/' +
                selectedTableConfig[index].imageValue +
                '"/>';
            }
          }
          break;
      }
    }

    const presenterHtml =
      '<div class="location">' +
      '<div class=icon-wrapper><div class=cell-icon>' +
      icon +
      '</div></div><div class=icon-text-wrapper><div class="main-title truncated-text">' +
      serviceType +
      '</div><div class="sub-title truncated-text">' +
      subLabel +
      '<div class="troubleshooting"> <span>' +
      subLabelText +
      '</span></div></div>' +
      '<div class="sub-title truncated-text">' +
      subLabel1 +
      '</div></div></div>';

    return presenterHtml;
  };
  // eslint-disable-next-line
  cellclick(e, cell): any {
    const cellValue = cell.getValue()['testAssignedCount'] || cell.getValue();
    if (cellValue?.labelName === 'flowDetails') {
      if (cellValue?.details?.length) {
        const table = `
          <table class="test_assigned_container" id="test-assigned">`;
        let rows = '';
        cellValue?.details.forEach(item => {
          rows =
            rows +
            `<tr>
              <td>${item.fieldName}</td>
              <td>${item.value}</td>
            </tr>`;
        });
        const tablecontent = `${table}${rows}</table>`;
        return tablecontent;
      }
    } else {
      if (cellValue?.length && cellValue[0]?.showProgressBar === 'none') {
        cellValue.forEach(element => {
          element['class_name'] = element.used === 0 ? 'disable_view' : 'testlist_view';
        });
        let table = `
        <table class="test_assigned_container" id="test-assigned"><tr><div>
        <th class="table_header">Type</th>
        <th class="table_header">Used</th>
        <th class="table_header">Max</th></div>
      </tr>`;
        let rows = '';
        cellValue.forEach(item => {
          const iconConfig = iconConfiguration[0]['tests'].find(test =>
            test.key.includes(item.type)
          );
          let imageValue = '';
          if (iconConfig?.imageValue) {
            imageValue = '<img class="popup-icon" src="images/' + iconConfig.imageValue + '"/>';
          } else {
            imageValue = `<i class="popup-icon mdi ${iconConfig?.iconValue}"></i>`;
          }
          rows =
            rows +
            `<tr><td class="type">${imageValue}<span>${item.type}</span></td>
          <td>${item.used}</td>
          <td>${item.max}</td>
          <td style=display:${item.hideView ? 'none' : 'table-cell'} class="${
              item.class_name
            }"><div id="view" class="${item.type}"><span class="${item.class_name} ${
              item.type
            }"><i class="mdi mdi-pulse ${item.type}"></i>View</span></div></td></tr>`;
        });
        table = `${table}${rows}</table>`;
        return table;
      }
    }
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getRow = (cell, formatter): string => {
    if (formatter.type == 'nested-icon') {
      let cellValue = cell.getRow().getData()[formatter.columnName];
      if (formatter?.columnType === 'image') {
        cellValue = this.getImageURL(cellValue);
      }
      if (cellValue) {
        const icon = cellValue?.image ? cellValue.image : cellValue?.icon;
        const image = icon ? `<img class="tabulator-icon" alt="" src=${icon}>` : '';
        const label = cellValue.label ? DOMPurify.sanitize(cellValue.label) : '';
        const subLabel = cellValue.sublabel ? DOMPurify.sanitize(cellValue.sublabel) : '';
        const extra = cellValue.extra ? DOMPurify.sanitize(cellValue.extra) : '';
        const expired = cellValue.expired ? DOMPurify.sanitize(cellValue.expired) : '';
        const className = cellValue.upgradeClass ?? '';
        const labelClass = cellValue.labelClass ?? '';
        return `<div class="location">
      <div class="icon-wrapper">${image}</div>
      <div class="icon-text-wrapper">
        <div class="main-title truncated-text ${labelClass}">${label}</div>
        <div class="sub-title truncated-text ${className}">${subLabel}</div>
        <div class="sub-title truncated-text ${className}">${extra}</div>
           <div class="sub-title truncated-text expired-text">${expired}</div>
      </div>
      </div>`;
      }
    } else if (formatter.type === 'progress-bar') {
      const cellValue = cell.getRow().getData()[formatter.columnName];
      return this.getProgressbarTestAssigned(cellValue);
    } else if (formatter.type === 'timestamp-logs') {
      const cellValue = cell.getRow().getData()[formatter.columnName];
      return this.getTimestampLogs(cellValue);
    } else if (formatter.type === 'flow-details') {
      return `<div>more...</div>`;
    } else {
      return (
        '<div class="location">' +
        '<img class="tabulator-icon" alt="" src=' +
        cell?.getValue()?.icon +
        '  /><div>' +
        cell?.getValue() +
        '</div></div>'
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  columnFormatter = (cell, formatter): string => {
    let htmlFormat = '';
    const cellValue = cell.getRow().getData()[formatter.columnName];
    const label = cellValue.col1.label ? cellValue.col1.label : '';
    const subLabel = cellValue.col1.sublabel ? cellValue.col1.sublabel : '';
    const extra = cellValue.col1.extra ? cellValue.col1.extra : '';
    const col2Label = cellValue.col2.label ? cellValue.col2.label : '';
    const col2SubLabel = cellValue.col2.sublabel ? cellValue.col2.sublabel : '';
    const col2Extra = cellValue.col2.extra ? cellValue.col2.extra : '';
    const className = cellValue.upgradeClass ?? '';
    const labelClass = cellValue.labelClass ?? '';
    if (label || subLabel || extra) {
      htmlFormat = `<div class="icon-text-wrapper">
        <div class="main-title truncated-text ${labelClass}">${label}</div>
        <div class="sub-title truncated-text ${className}">${subLabel}</div>
        <div class="sub-title truncated-text ${className}">${extra}</div>
      </div>`;
    }
    if (col2Label || col2SubLabel || col2Extra) {
      htmlFormat += `<div class="icon-text-wrapper col2">
        <div class="main-title truncated-text ${labelClass}">${col2Label}</div>
        <div class="sub-title truncated-text ${className}">${col2SubLabel}</div>
        <div class="sub-title truncated-text ${className}">${col2Extra}</div>
      </div>`;
    }
    return `<div class="location">${htmlFormat}</div>`;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getActionIcon = (cell, formatter): string => {
    const actions = cell.getValue();
    if (actions) {
      return `
      <button  aria-label="Action menu" class="mat-menu-trigger btn-menu-trigger actions-btn mat-button mat-button-base" aria-haspopup="true"><span class="mat-button-wrapper"><i  class="material-icons">more_horiz</i></span><span matripple="" class="mat-ripple mat-button-ripple"></span><span class="mat-button-focus-overlay"></span></button>
    `;
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getActionLabel(): string {
    return `
      <button  aria-label="Action menu" class="mat-menu-trigger btn-menu-trigger actions-btn mat-button mat-button-base" aria-haspopup="true"><span class="mat-button-wrapper action-select">Select</span><span matripple="" class="mat-ripple mat-button-ripple"></span><span class="mat-button-focus-overlay"></span></button>
    `;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getSpecsLabel(): string {
    return `
        <button  aria-label="Action menu" class="mat-menu-trigger btn-menu-trigger actions-btn mat-button mat-button-base" aria-haspopup="true"><span class="mat-button-wrapper action-select">Specs</span><span matripple="" class="mat-ripple mat-button-ripple"></span><span class="mat-button-focus-overlay"></span></button>
      `;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getIconLabelText = (cell, formatter): string => {
    const cellText = cell.getValue().text;
    if (cellText) {
      return (
        `
        <div class="btn-container">
        <div class="new_btn">
          <i class="mdi-icons mdi ` +
        cell.getValue().icon +
        `"></i> <a>` +
        cell.getValue().text +
        `</a>
        </div>
      </div>`
      );
    }
    return '';
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getFormattedDate = (cell, formatter) => {
    const dateValue = this.getValue(cell.getRow().getData(), formatter.columnName);
    return Number.isInteger(dateValue) ? this.dateFormatService.getDateTime(dateValue) : dateValue;
  };

  getUTCDateFormatter = (cell, formatter) => {
    const dateValue = this.getValue(cell.getRow().getData(), formatter.columnName);
    return Number.isInteger(dateValue) ? this.dateFormatService.getDateTime(dateValue) : dateValue;
  };

  getDateFormat(timeStamp: number): string {
    return this.dateFormatService.getLocaleDateString(timeStamp);
  }
  getTimeFormat(timeStamp: number): string {
    return this.dateFormatService.getTime(timeStamp);
  }

  // eslint-disable-next-line
  formatValueWithUnit(cell, formatter, kpiUnit: string, unit: string): string {
    const dateValue = this.getValue(cell.getRow().getData(), formatter.columnName);
    switch (unit) {
      case 'time':
        return this.formatService.formatValueWithUnit(dateValue, UNIT[kpiUnit]);
      case 'percent':
        return this.formatService.formatValueWithUnit(dateValue, UNIT.percentage);
      case 'round': {
        return round(dateValue, 2);
      }
      default: {
        return this.formatService.formatValueWithUnit(dateValue, UNIT.bitsPerSecond);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMatIconText = (cell, formatterParams): string => {
    const cellValue = cell.getRow().getData()[formatterParams.columnName];
    const cellText = cellValue.label;
    const iconName = cellValue?.icon;
    const title = cellValue?.title ?? '';
    if (cellText) {
      return `<div class="location">
      <div class="icon-wrapper" title="${title}" >
        <i class="tabulator-icon material-icons icon" >${iconName}</i>
       </div>
      <div class="icon-text-wrapper">
        <div class="main-title truncated-text">${cellText}</div>
      </div>
      </div>`;
    }
    return '';
  };

  // eslint-disable-next-line
  getImageURL = (entity: any) => {
    if (entity.url && entity.url.indexOf('tcp:') < 0) {
      this.logoWidgetService.getImage(entity.url, entity.icon, (originalUrl, imgData) => {
        if (entity.url !== originalUrl) {
          // if your input to logo.widget is bound to rapidly updating input value, sometimes you will get
          // responses from backend in incorrect order (since they are coming back asynchronously!)
          return;
        }
        entity.image = imgData;
      });
    }
    return entity;
  };
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getValue = (obj, key) => {
    const keyParts = key.split('.');
    return this.getValueHelper(obj, keyParts);
  };
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getValueHelper = (obj, keyParts) => {
    if (keyParts.length == 0) return obj;
    const key = keyParts.shift();
    if (!obj[key]) {
      return null;
    }
    if (Array.isArray(obj[key])) {
      return obj[key].map(x => this.getValueHelper(x, [...keyParts])).flat();
    }
    return this.getValueHelper(obj[key], [...keyParts]);
  };
  // eslint-disable-next-line
  customFilter = (headerValue: any, rowValue: any, rowdata: any, filterparams: any): boolean => {
    let columnValues = [];
    filterparams?.extraValueForFilter.forEach(element => {
      columnValues.push(
        this.getValue(rowdata, element.fields) ? this.getValue(rowdata, element.fields) : ''
      );
    });
    if (headerValue.length > 0) {
      if (filterparams.isDate) {
        columnValues = columnValues?.map(element => {
          return this.dateFormatService.getDateTime(element);
        });
      }
      const result = columnValues.find(v => v.toLowerCase().includes(headerValue.toLowerCase()));
      return result ? true : false;
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  groupHeader(value, count, data, group: GroupComponent) {
    const field = group.getField().includes('.')
      ? group.getField().split('.')[0]
      : group.getField();
    let imgUrl = '';
    let dynmamicClass = '';
    const iconConfig = iconConfiguration[0][field]
      ? iconConfiguration[0][field].find(x => x.key == value)
      : undefined;
    dynmamicClass = iconConfiguration[0][field]
      ? iconConfiguration[0][field].find(x => x.key == value)?.customClass
      : '';
    const dataErrorClass = value ? '' : 'bg-error';
    value = value ? value : '';
    if (iconConfig?.iconValue) {
      return (
        '<span style=margin-left:5px class=' +
        dataErrorClass +
        '>' +
        '<i class="tabulator-icon logo mdi ' +
        iconConfig.iconValue +
        ' ' +
        dynmamicClass +
        '"></i><span style=margin-left:0>' +
        value +
        ' (' +
        count +
        ' item)</span> </span>'
      );
    } else if (iconConfig?.imageValue) {
      return (
        '<span style=margin-left:5px class=' +
        dataErrorClass +
        '>' +
        '<img class="tabulator-icon ' +
        dynmamicClass +
        '" src="images/' +
        iconConfig.imageValue +
        '"/><span style=margin-left:0>' +
        value +
        ' (' +
        count +
        ' item)</span> </span>'
      );
    } else if (!iconConfig) {
      if (data.length > 0) {
        imgUrl = data[0][field]?.image ? data[0][field].image : data[0][field]?.icon;
      } else if (data.length == 0) {
        const filteredData = group
          .getTable()
          .getData()
          .find(x => x[field]['label'] == group.getKey());

        imgUrl = filteredData
          ? filteredData[field].image
            ? filteredData[field].image
            : filteredData[field].icon
          : undefined;
      }
      if (imgUrl)
        return (
          '<span class=' +
          dataErrorClass +
          '>' +
          '<img class="tabulator-icon group-icon logo" alt=" " src=' +
          imgUrl +
          '><span class=group-header-title>' +
          value +
          ' (' +
          count +
          ' item)' +
          '</span></span>'
        );
      else
        return (
          '<span class=' +
          dataErrorClass +
          '><span class=group-header-title>' +
          value +
          ' (' +
          count +
          ' item)' +
          '</span></span>'
        );
    }
  }

  getTestTypeImage(key: string): string {
    switch (key) {
      case 'voip':
        return 'mdi mdi-phone-voip';
      case 'truespeed':
        return 'mdi mdi-speedometer';
      case 'service':
        return 'mdi mdi-web';
      case 'network':
        return 'mdi mdi-lan-connect';
      case 'multistep':
        return 'mdi mdi-script-text-outline';
    }
  }
  //eslint-disable-next-line
  populateTestAssignedDataSource(testAssignedData: any, deviceName: string): any {
    const dataSource = [];
    for (const key in testAssignedData) {
      if (testAssignedData[key]?.total) {
        dataSource.push({
          type: key,
          used: testAssignedData[key]?.used,
          max: testAssignedData[key]?.total,
          className: this.getTestTypeImage(key),
          maxCount: testAssignedData['maxCount'],
          overalUsed: testAssignedData['overall_total_used'],
          overalPercentage: Number.isNaN(testAssignedData['overall_percentage'])
            ? -1
            : testAssignedData['overall_percentage'],
          deviceName: deviceName,
          hasOveralPercentage: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'block'
            : 'none',
          showProgressBar: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'none'
            : 'block'
        });
      }
    }
    return dataSource;
  }

  // eslint-disable-next-line
  commonTableConfiguration(): any {
    return {
      minHeight: 165, //do not let table get smaller than 300 px heigh
      height: '720px',
      // width: '100%',
      groupStartOpen: false,
      rowHeight: 50,
      layout: 'fitColumns',
      // selectableRangeMode: 'click',
      selectable: true,
      // resizableColumnFit: true,
      pagination: true,
      paginationSize: 10,
      paginationSizeSelector: [10, 20, 50, 100],
      paginationCounter: 'rows',
      paginationButtonCount: 3,
      groupToggleElement: 'header', //toggle group on click anywhere in the group header,
      initialSort: [{ column: 'created', dir: 'desc' }],
      persistence: {
        sort: true,
        group: {
          groupBy: true,
          groupStartOpen: true,
          groupHeader: true
        },
        page: true
      },
      persistenceID: 'atweb',
      columnDefaults: {
        title: 'test',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tooltip: (event, cell, onRendered) => {
          if (!cell.getRow().isSelected()) {
            const theme = sessionStorage.getItem('theme') === 'dark' ? 'dark-theme' : '';
            const elParent = document.createElement('div');
            elParent.id = 'tabulator-custom-tooltip';
            elParent.className = `icon-text-wrapper ${theme}`;
            const el = document.createElement('div');
            el.className = 'tabulator-tooltip main-title';
            el.innerText = event.target.innerText;
            elParent.appendChild(el);
            if (el.innerText == '') return;
            return elParent;
          } else {
            return;
          }
        }
      },
      selectableCheck: row => {
        return !row.getData().isCheckboxDisabled;
      },
      placeholder: 'No Data Available'
    };
  }

  //eslint-disable-next-line
  getFlowMoreDetails(cellValue: any): any {
    const table = `
        <table class="test_assigned_container" id="test-assigned">`;
    let rows = '';
    cellValue.forEach(item => {
      rows =
        rows +
        `<tr>
            <td>${item.fieldName}</td>
            <td>${item.Value}</td>
          </tr>`;
    });

    const tablecontent = `${table}${rows}</table>`;
    return tablecontent;
  }

  //eslint-disable-next-line
  getProgressbarTestAssigned(cellValue: any): any {
    let innerDiv = '';
    if (cellValue?.length) {
      cellValue.forEach(item => {
        innerDiv =
          innerDiv +
          `<div><div>${item.type}</div>
        <div>${item.used}</div>
        <div>${item.max}</div>
        </div>`;
      });
      return `<div class="progress_container" style="display:${
        cellValue[0]?.hasOveralPercentage
      }; width: ${cellValue[0]?.width ? cellValue[0]?.width : '70%'}">
      <div>
      <progress class="progress_bar" value="${
        cellValue[0]?.overalPercentage
      }" max="100"></progress><span class="overal-percentage">${
        cellValue[0]?.overalPercentage
      }%</span>
      </div>
      <div class="count_row">
      <div>${cellValue[0]?.overalUsed} of ${cellValue[0]?.maxCount} used</div>
      </div>
      <div class="tooltip">
          <div>${innerDiv}</div>
      </div>
      </div>
      <div style="display:${cellValue[0]?.showProgressBar}">-------------</div>`;
    }
  }

  //eslint-disable-next-line
  getTimestampLogs(cellValue: any): any {
    let innerDiv = '';
    if (cellValue?.length) {
      cellValue.forEach(item => {
        innerDiv =
          innerDiv +
          `<div style="text-decoration: underline"><a id="logs" name="${item.url}">${item.timestamp}</a></div></br>`;
      });
      return innerDiv;
    }
  }
}
